import { GA4_MEASUREMENT_ID, GTM_CONTAINER_ID, META_PIXEL_ID } from "./marketingIds";

type ScriptSpec = {
  readonly id: string;
  readonly src: string;
};

const injectScript = ({ id, src }: ScriptSpec): void => {
  if (typeof document === "undefined") return;
  if (document.getElementById(id)) return;

  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
};

const ensureDataLayer = (): unknown[] => {
  if (typeof window === "undefined") return [];
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

const ensureGtag = (): void => {
  if (typeof window === "undefined") return;
  ensureDataLayer();

  if (typeof window.gtag === "function") return;

  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer?.push(args);
  };
};

const initGtm = (): void => {
  if (typeof window === "undefined") return;

  const dl = ensureDataLayer();
  dl.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

  injectScript({
    id: "iehub-gtm",
    src: `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(
      GTM_CONTAINER_ID
    )}`,
  });
};

const initGa4 = (): void => {
  if (typeof window === "undefined") return;

  ensureGtag();

  injectScript({
    id: "iehub-ga4",
    src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
      GA4_MEASUREMENT_ID
    )}`,
  });

  // Push initial config onto dataLayer (mirrors legacy behavior)
  window.gtag?.("js", new Date());
  window.gtag?.("config", GA4_MEASUREMENT_ID);
};

const initMetaPixel = (): void => {
  if (typeof window === "undefined") return;
  if (typeof window.fbq === "function") return;

  // Mirror Meta Pixel bootstrap snippet (without inline HTML)
  const fbq = function fbq(...args: unknown[]) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const self = fbq as any;
    if (self.callMethod) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (self.callMethod as any).apply(self, args as any);
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (self.queue as any[]).push(args);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any)._fbq = fbq;
  window.fbq = fbq;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (fbq as any).push = fbq;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (fbq as any).loaded = true;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (fbq as any).version = "2.0";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (fbq as any).queue = [];

  injectScript({
    id: "iehub-meta-pixel",
    src: "https://connect.facebook.net/en_US/fbevents.js",
  });

  window.fbq?.("init", META_PIXEL_ID);
  window.fbq?.("track", "PageView");
};

/**
 * Initialize all marketing tags (GTM, GA4, Meta Pixel).
 * Safe to call multiple times; scripts are injected once.
 */
export const initMarketingTags = (): void => {
  initGtm();
  initGa4();
  initMetaPixel();
};

export type TrackPageViewParams = {
  readonly pagePath: string;
  readonly pageTitle: string;
  readonly pageLocation: string;
};

/**
 * SPA virtual page view tracking for HashRouter route changes.
 */
export const trackPageView = ({ pagePath, pageTitle, pageLocation }: TrackPageViewParams): void => {
  if (typeof window === "undefined") return;

  // Meta Pixel
  try {
    window.fbq?.("track", "PageView");
  } catch {
    // ignore
  }

  // GA4 (gtag)
  try {
    window.gtag?.("event", "page_view", {
      page_path: pagePath,
      page_location: pageLocation,
      page_title: pageTitle,
    });
  } catch {
    // ignore
  }

  // GTM (dataLayer) — helpful for virtual page triggers
  try {
    window.dataLayer?.push({
      event: "virtualPageView",
      page_path: pagePath,
      page_location: pageLocation,
      page_title: pageTitle,
    });
  } catch {
    // ignore
  }
};




